import "./InstanceResultsPage.scss";

import {
  ChevronStartIcon,
  Flex,
  InfoIcon,
  Segment,
  Text,
  Tooltip,
} from "@fluentui/react-northstar";
import {
  NpsScore,
  calculateNpsScore,
  getImageCardUrl,
  getNpsCategory,
  getQuestionResponses,
  getSurveyTypeDisplay,
} from "@teams/cards";
import { NominalQuestion, OrdinalQuestion, OrdinalQuestionChoice } from "@teams/types";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router";

import { useSeries } from "../../_shared/actions/useSeries";
import { AlertError } from "../../_shared/components/AlertError";
import { AnalysisHeader } from "../../_shared/components/AnalysisHeader";
import { TeamsLoader } from "../../_shared/components/TeamsLoader";
import { capitalizeFirstLetter, getPageContext } from "../../_shared/utils/teamsHelpers";
import { useSurvey } from "../actions/useSurvey";
import { useUserInfo } from "../actions/useUserInfo";
import { AutolinkIntegrationCell } from "../components/AutolinkIntegrationCell";
import { NavBarNew } from "../components/NavBar/NavBarNew";
import { NavigationHeader } from "../components/NavigationHeader";
import { ResponseTable } from "../components/ResponseTable";
import { isError } from "../services/ChannelApi";
import { useTeamsContext } from "../state/TeamsContextProvider";
import { useTheme } from "../state/ThemeProvider";

export const InstanceResultsPage: React.FC = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const { teamsContext } = useTeamsContext();
  const { themeName } = useTheme();
  const [choiceLabels, setChoiceLabels] = useState<Map<string, string>>();
  const [questionNpsScores, setQuestionNpsScores] = useState<Map<string, NpsScore>>();
  const { loadSurvey, isSurveyLoading, survey, surveyError, surveyResponses } = useSurvey();
  const { error, isSeriesLoading, series, loadSeries } = useSeries();
  const { user } = useUserInfo();
  const history = useHistory();

  const loadSurveyCallback = useCallback(async () => {
    if (teamsContext && surveyId) {
      const s = await loadSurvey(teamsContext, surveyId, { loadResponses: true });
      if (!isError(s) && s.survey.scheduleId) {
        await loadSeries(teamsContext, s.survey.scheduleId);
      }
    }
  }, [teamsContext, loadSurvey, surveyId, loadSeries]);

  useEffect(() => {
    loadSurveyCallback();
  }, [loadSurveyCallback]);

  useEffect(() => {
    if (!survey) {
      return;
    }
    let choiceLabels: Map<string, string> = new Map<string, string>();
    let questionNpsMap: Map<string, NpsScore> = new Map<string, NpsScore>();
    survey.questions.forEach(question => {
      if (question.questionType === "nominal") {
        (question as NominalQuestion).choices.forEach(choice => {
          choiceLabels.set(choice.id, choice.value);
        });
      }
      if (question.questionType === "ordinal") {
        const ordinalQuestion = question as OrdinalQuestion;
        if (ordinalQuestion.renderAs === "nps" && surveyResponses) {
          const userResponses = getQuestionResponses(surveyResponses, question.id);
          const npsScore = calculateNpsScore(ordinalQuestion.choices, userResponses);
          questionNpsMap.set(question.id, npsScore);
          ordinalQuestion.choices.forEach(choice => {
            choiceLabels.set(choice.id, getNpsChoiceLabel(choice));
          });
        } else {
          ordinalQuestion.choices.forEach(choice => {
            choiceLabels.set(choice.id, choice.label);
          });
        }
      }
    });
    setQuestionNpsScores(questionNpsMap);
    setChoiceLabels(choiceLabels);
  }, [survey, surveyId, themeName, surveyResponses]);

  const columns = useMemo(() => {
    let headers: any[] = [
      { Header: "Name", accessor: "name" },
      {
        Header: "Completed Time",
        accessor: "completedOn",
        sortType: "datetime",
        Cell: (props: any) => moment(props.value as Date).format("MMM Do LT"),
      },
    ];
    if (survey) {
      survey.questions.forEach(question => {
        const nps = questionNpsScores?.get(question.id)?.nps;
        const npsLabel = nps ? `NPS (${nps}): ` : "";
        headers.push({
          Header: () => {
            const questionLengthLimit = 50;
            if (question.label.length > questionLengthLimit) {
              return (
                <>
                  <span className={getNpsLabelClass(nps)}>{npsLabel}</span>
                  {`${question.label.substr(0, questionLengthLimit)}…`}
                  <Tooltip
                    trigger={
                      <InfoIcon
                        outline
                        xSpacing="none"
                        size="small"
                        className="instance-results__tooltip"
                      />
                    }
                    content={question.label}
                  />
                </>
              );
            } else {
              return (
                <>
                  <span className={getNpsLabelClass(nps)}>{npsLabel}</span>
                  {question.label}
                </>
              );
            }
          },
          accessor: question.id,
        });
      });
      if (survey.commentsEnabled) {
        headers.push({ Header: "Comments", accessor: "comments" });
      }
    }

    return headers;
  }, [survey, questionNpsScores]);

  const data = useMemo(() => {
    if (!surveyResponses) {
      return [];
    }

    return surveyResponses.map(entry => {
      let row: any = {};
      row.name = entry.user.name;
      row.completedOn = moment(entry.completedOn).toDate();
      entry.responses.forEach(response => {
        const responseText = Array.isArray(response.value)
          ? response.value
              .map(value => (choiceLabels && choiceLabels.get(value)) ?? value)
              .join(", ")
          : (choiceLabels && choiceLabels.get(response.value)) ?? response.value;

        row[response.questionId] = (
          <AutolinkIntegrationCell
            text={responseText}
            autolinkIntegrations={series?.autolinkIntegrations}
          />
        );
      });
      row.comments = entry.comments.map(comment => `${comment.value} `) || "";
      return row;
    });
  }, [surveyResponses, choiceLabels, series]);

  return (
    <Flex column gap="gap.medium" className="instance-results">
      <NavBarNew />

      <NavigationHeader
        breadcrumbs={[
          {
            icon: <ChevronStartIcon />,
            title: "Back",
            link: "",
            onClick: e => {
              e.preventDefault();
              history.goBack();
            },
          },
        ]}
      />

      <TeamsLoader isLoading={isSeriesLoading || isSurveyLoading} loadingMessage="Loading...">
        {error && <AlertError error={error} onClick={() => {}} />}
        {surveyError && <AlertError error={surveyError} onClick={() => {}} />}
        {survey && !surveyError && (
          <Flex column gap="gap.medium">
            <Segment className="instance-results__container">
              {series && (
                <AnalysisHeader
                  user={user}
                  series={series}
                  survey={survey}
                  onChanged={() => loadSurveyCallback()}
                />
              )}
              <div>
                <Text
                  className="instance-results__header-label"
                  content="Completions:"
                  weight="bold"
                />
                <Text content={`👤 ${survey.responseCount}`} />
              </div>
              {surveyResponses && surveyResponses.length === 0 && (
                <div className="instance-results__no-responses">
                  <div className="instance-results__no-responses-text">No responses yet</div>
                  <img
                    className="instance-results__no-responses-image"
                    src={getImageCardUrl(
                      "NO_VOTES",
                      getPageContext() === "/agile" ? "agile" : "msteams"
                    )}
                    alt="No responses yet"
                  />
                </div>
              )}
              {surveyResponses && surveyResponses.length > 0 && (
                <>
                  <div className="instance-results__sub-heading">
                    <Text
                      size="large"
                      weight="bold"
                      content={`${getSurveyTypeDisplay(survey.type, {
                        capitalize: true,
                      })} responses`}
                    />
                  </div>
                  <div className="instance-results__response-table">
                    <ResponseTable
                      columns={columns}
                      data={data}
                      sortBy={[
                        {
                          id: "completedOn",
                          desc: true,
                        },
                      ]}
                    />
                  </div>
                </>
              )}
            </Segment>
          </Flex>
        )}
      </TeamsLoader>
    </Flex>
  );
};

const getNpsChoiceLabel = (choice: OrdinalQuestionChoice) => {
  const category = getNpsCategory(choice.value);
  return `${capitalizeFirstLetter(category)} (${choice.value})`;
};

const getNpsLabelClass = (score: number = 0) => {
  if (score < 0) {
    return `instance-results__nps-label-detractor`;
  } else if (score > 0) {
    return `instance-results__nps-label-promoter`;
  } else {
    return `instance-results__nps-label-passive`;
  }
};
